<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick, defineAsyncComponent } from 'vue'

// Dynamically load mobile components
const BottomLinks = defineAsyncComponent(() =>
  import('./mobile/bottomLinks.vue')
)

const MobileMenu = defineAsyncComponent(() => 
  import('./mobile/menu.vue')
)

// Define props
const props = defineProps({
  navigation: {
    type: Array,
    default: () => []
  },
  additionalLanguages: {
    type: Array,
    default: () => []
  },
  siteHandle: {
    type: [String],
    default: ''
  },
  homeLink: {
    type: String,
    default: '/'
  },
  visibilityOptions: {
    type: String,
    default: ''
  },
  customHeaderCta: {
    type: Object,
    default: () => ({})
  },
  globalCtaSettings: {
    type: Object,
    default: () => ({})
  },
})

// Computed
// navItems: navigation items excluding buttons (typeHandle === 'navButton')
const navItems = computed(() => {
  const list = Array.isArray(props.navigation) ? props.navigation : []
  return list.filter(item => item?.typeHandle !== 'navButton')
})

// navButtons: navigation items that are buttons (typeHandle === 'navButton')
const navButtons = computed(() => {
  const list = Array.isArray(props.navigation) ? props.navigation : []
  return list.filter(item => item?.typeHandle === 'navButton')
})

// Define reactive state
const menuIsOpen = ref(false)
const hamburgerButtonRef = ref(null)
const mobileNavRef = ref(null)
let restoreFocusEl = null

// Scroll tracking
const isScrolled = ref(false)

const handleScroll = () => {
  isScrolled.value = window.scrollY > 100
}

// Define method to toggle mobile menu
function toggleMobileMenu() {
  menuIsOpen.value = !menuIsOpen.value
}

const closeMobileMenu = () => {
  menuIsOpen.value = false
}

const getFocusableElements = (rootEl) => {
  if (!rootEl) return []
  const selector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',')

  return Array.from(rootEl.querySelectorAll(selector)).filter((el) => {
    if (!(el instanceof HTMLElement)) return false
    if (el.hidden) return false
    const hasLayout = el.offsetParent !== null || el.getClientRects().length > 0
    return hasLayout
  })
}

const trapFocusInMobileNav = (event) => {
  if (event.key !== 'Tab') return
  const rootEl = mobileNavRef.value
  if (!rootEl) return

  const focusables = getFocusableElements(rootEl)
  if (!focusables.length) {
    event.preventDefault()
    rootEl.focus()
    return
  }

  const first = focusables[0]
  const last = focusables[focusables.length - 1]
  const active = document.activeElement

  if (event.shiftKey && active === first) {
    event.preventDefault()
    last.focus()
    return
  }

  if (!event.shiftKey && active === last) {
    event.preventDefault()
    first.focus()
  }
}

const handleMobileNavKeydown = (event) => {
  if (!menuIsOpen.value) return

  if (event.key === 'Escape' || event.keyCode === 27) {
    event.preventDefault()
    event.stopPropagation()
    closeMobileMenu()
    return
  }

  trapFocusInMobileNav(event)
}

// Recompute scrollable height when menu opens (banner may have mounted by then)
watch(menuIsOpen, (isOpen) => {
  if (isOpen) {
    restoreFocusEl = document.activeElement instanceof HTMLElement ? document.activeElement : null
    // Save current scroll position
    const scrollY = window.scrollY
    document.body.style.position = 'fixed'
    document.body.style.top = `-${scrollY}px`
    document.body.style.width = '100%'
    document.body.style.overflow = 'hidden'
    // Store scroll position for restoration
    document.body.dataset.scrollY = scrollY.toString()

    nextTick(() => {
      const rootEl = mobileNavRef.value
      if (!rootEl) return
      rootEl.setAttribute('tabindex', '-1')
      const focusables = getFocusableElements(rootEl)
      ;(focusables[0] || rootEl).focus?.()
    })

    document.addEventListener('keydown', handleMobileNavKeydown, true)
  } else {
    // Restore scroll position
    const scrollY = document.body.dataset.scrollY || '0'
    document.body.style.position = ''
    document.body.style.top = ''
    document.body.style.width = ''
    document.body.style.overflow = ''
    // Use immediate scroll without smooth animation
    window.scrollTo({ top: parseInt(scrollY, 10), behavior: 'auto' })
    delete document.body.dataset.scrollY

    document.removeEventListener('keydown', handleMobileNavKeydown, true)
    try {
      ;(restoreFocusEl || hamburgerButtonRef.value)?.focus?.()
    } catch {
      // noop
    }
  }
})

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
  handleScroll()
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleMobileNavKeydown, true)
})

</script>

<!-- -------------------------------------------------------------------------------------------------
    Template
------------------------------------------------------------------------------------------------- -->

<template>
  <header :class="{ 'is-scrolled': isScrolled || menuIsOpen }">
    <div class="header-inner">
      <div class="header-mobile container">

      <!-- Mobile Logo -->
      <a :href="homeLink" class="logo" aria-label="Home">
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
          <path d="M18.2188 13.7813H32V18.2188H21.3571L28.8828 25.7444L25.7455 28.8828L18.2188 21.3549V32H13.7813V21.356L6.25558 28.8828L3.11719 25.7455L10.644 18.2188H0V13.7813H10.6451L3.1183 6.25446L6.25558 3.11719L13.7813 10.6429V0H18.2188V13.7813Z" fill="black"/>
          <path d="M28.3254 6.46697C28.3254 8.03578 27.0536 9.30755 25.4848 9.30755C23.916 9.30755 22.6443 8.03578 22.6443 6.46697C22.6443 4.89817 23.916 3.6264 25.4848 3.6264C27.0536 3.6264 28.3254 4.89817 28.3254 6.46697Z" fill="black"/>
        </svg>
      </a>

      <!-- Mobile Menu -->
      <div class="right">

        <GlobalsLocaleSelector
          v-if="!['onlyTopBanner', 'hideEverything', 'showOnlyCtaWithoutLangSwitcher'].includes(visibilityOptions)"
          :additionalLanguages="additionalLanguages"
          :siteHandle="siteHandle"
        />

        <button
          v-if="!['onlyTopBanner', 'hideEverything'].includes(visibilityOptions) ||
            (customHeaderCta && customHeaderCta.url && customHeaderCta.title)"
          class="hamburger"
          type="button"
          ref="hamburgerButtonRef"
          @click='toggleMobileMenu'
          :aria-expanded='menuIsOpen.toString()'
          aria-controls="mobile-navigation"
          :aria-label='menuIsOpen ? "Close the Main Menu" : "Open the Main Menu"'>
          <transition name="fade" mode="out-in">
            <svg v-if="!menuIsOpen" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true" focusable="false">
              <path d="M4 24H28V21.3333H4V24ZM4 17.3333H28V14.6667H4V17.3333ZM4 8V10.6667H28V8H4Z" fill="var(--color-gray-900)"/>
            </svg>

            <svg v-else xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true" focusable="false">
              <path d="M25.3333 8.5465L23.4533 6.6665L16 14.1198L8.54663 6.6665L6.66663 8.5465L14.12 15.9998L6.66663 23.4532L8.54663 25.3332L16 17.8798L23.4533 25.3332L25.3333 23.4532L17.88 15.9998L25.3333 8.5465Z" fill="var(--color-gray-900)"/>
            </svg>
          </transition>
        </button>

      </div>

      <!-- Mobile Navigation -->
      <transition name="fade">
        <nav v-if="menuIsOpen"
          id="mobile-navigation"
          ref="mobileNavRef"
          class="mobile-navigation"
          role="navigation"
          aria-label="Main navigation">
          <div class="scrollable">

            <MobileMenu
              v-if="!['onlyTopBanner', 'hideEverything','noTopBannerNoNavigation', 'showTopBannerHideNavigation', 'showOnlyCta', 'showOnlyCtaWithoutLangSwitcher'].includes(visibilityOptions)"
              :navigation="navItems"
            />

            <div v-if="customHeaderCta && customHeaderCta.url && customHeaderCta.title"
              class="mobile-custom-header-cta-container container"
            >
              <GlobalsButton
                :url="customHeaderCta.url"
                :title="customHeaderCta.title"
                class="mobile-custom-header-cta"
                :class="customHeaderCta.class"
              />
            </div>
            <BottomLinks
              v-else-if="navButtons.length > 0 && !['onlyTopBanner', 'hideEverything', 'hideCta', 'hideCtasAndHideBanner'].includes(visibilityOptions)"
              :nav-buttons="navButtons"
              :global-cta-settings="globalCtaSettings"
              :visibility-options="visibilityOptions"
            />
          </div>
        </nav>
      </transition>

      </div>
    </div>
  </header>
</template>

<!-- -------------------------------------------------------------------------------------------------
    Styling
------------------------------------------------------------------------------------------------- -->

<style scoped>

header {
  position: sticky;
  z-index: 100;
  top: 0;
  width: 100%;
  color: var(--color-gray-900);

  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 1px;
    background: var(--color-gray-150);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &.is-scrolled::before {
    opacity: 1;
  }
}

.header-mobile {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 72px;
}

.mobile-navigation {
  position: absolute;
  top: 72px;
  left: 0;
  width: 100%;
  height: calc(100dvh - 72px);
  background-color: var(--color-white);
  border-top: 1px solid #d5d8e5;
}

.scrollable {
  display: flex;
  flex-direction: column;
  position: relative;
  height: calc(100dvh - 72px);
  overflow-y: auto;
  overflow-x: hidden;
  padding-top: 12px;
  padding-bottom: 24px;
}

.right {
  display: flex;
  align-items: center;
}

.hamburger {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 10px;
}

.header-mobile :deep(.locale-selector__list) {
  border: 1px solid #0d0d0d;
  border-radius: 5px;
  margin-top: -4px;
  padding: 0;
  z-index: 10;
}

.header-mobile :deep(.locale-selector__link) {
  padding: 20px;
  font-size: 16px;
}

/* Logo */
.logo {
  position: relative;
  display: flex;
  align-items: center;
  height: 72px;
}

.mobile-custom-header-cta-container {
  display: flex;
  justify-content: center;
  margin-top: auto;
}

/* Styles moved to dynamically loaded component: mobile/bottomLinks.vue */

</style>
